import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Loader2, FileText, CalendarClock, PenLine, Check, UserPlus, Inbox } from 'lucide-react';
import { notificationsApi } from '../../lib/api';

/**
 * CENTRE DE NOTIFICATIONS (2026-09-21, demande d'Emeni).
 *
 * « À chaque demande de devis, ou rendez-vous, ou quand je reçois une confirmation, je dois avoir des
 * notifications avec une petite bulle rouge ; quand je clique, ça m'emmène sur la bonne page. »
 *
 * Chaque notification porte son lien interne, décidé au moment où l'événement s'est produit : c'est lui
 * qui ouvre la bonne fiche (le dossier concerné dans Suivi de prestation, le planning, Abonnements…).
 * Le composant ne devine rien — il suit ce que le serveur a écrit.
 *
 * La lecture est propre à chaque utilisateur (choix d'Emeni) : un collaborateur qui lit n'éteint pas la
 * pastille de son patron.
 */

interface Notif { id: string; type: string; title: string; body: string; link: string; created_at: string; lu: boolean }

const INTERVALLE_MS = 60000;

// Super-admin : les demandes des clients qui attendent une décision. Elles ne se « lisent » pas, elles se traitent
// (la bulle s'éteint quand la demande est acceptée ou refusée) ; leurs compteurs par page alimentent aussi les bulles du menu.
export const EVENEMENT_DEMANDES_ADMIN = 'demandesAdminCounts';
const estDemandeAdmin = (type: string) => type.startsWith('demande_');

const icone = (type: string) => {
    if (type === 'rdv_demande') return <CalendarClock size={15} />;
    if (type === 'signature_recue') return <PenLine size={15} />;
    if (type === 'demande_inscription' || type === 'demande_collaborateur') return <UserPlus size={15} />;
    if (estDemandeAdmin(type)) return <Inbox size={15} />;
    return <FileText size={15} />;
};

// « il y a 5 min », « hier » : plus parlant qu'une date complète dans une liste qu'on parcourt vite.
const depuis = (iso: string) => {
    try {
        const t = new Date(iso).getTime();
        const min = Math.round((Date.now() - t) / 60000);
        if (min < 1) return "à l'instant";
        if (min < 60) return `il y a ${min} min`;
        const h = Math.round(min / 60);
        if (h < 24) return `il y a ${h} h`;
        const j = Math.round(h / 24);
        if (j === 1) return 'hier';
        if (j < 7) return `il y a ${j} jours`;
        return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch { return ''; }
};

export function CentreNotifications({ compact = false }: { compact?: boolean }) {
    const navigate = useNavigate();
    const [ouvert, setOuvert] = React.useState(false);
    const [items, setItems] = React.useState<Notif[]>([]);
    const [nonLues, setNonLues] = React.useState(0);
    const [chargement, setChargement] = React.useState(false);
    const zone = React.useRef<HTMLDivElement>(null);
    const panneau = React.useRef<HTMLDivElement>(null);
    // Le panneau est posé dans <body> plutôt que sous le bouton (2026-09-21) : la barre latérale défile,
    // donc elle ROGNE tout ce qui dépasse d'elle — le panneau s'affichait coupé net à sa largeur, une
    // ligne de texte sur deux tronquée. Un élément fixé au document échappe à ce découpage ; sa position
    // est alors calculée à la main, à partir de celle du bouton.
    const [position, setPosition] = React.useState<{ left: number; top?: number; bottom?: number } | null>(null);

    const calculerPosition = React.useCallback(() => {
        const bouton = zone.current?.querySelector('button');
        if (!bouton) return;
        const r = bouton.getBoundingClientRect();
        const largeur = Math.min(330, window.innerWidth - 24);
        // Jamais hors de l'écran : on colle le panneau au bord quand la place manque à droite.
        const gauche = Math.max(12, Math.min(r.left, window.innerWidth - largeur - 12));
        // Sous le bouton dans l'en-tête mobile (compact), au-dessus dans la barre latérale : c'est là
        // qu'il y a la place, le bouton étant tout en bas.
        setPosition(compact ? { left: gauche, top: r.bottom + 8 } : { left: gauche, bottom: window.innerHeight - r.top + 8 });
    }, [compact]);

    const charger = React.useCallback(async () => {
        try {
            const r: any = await notificationsApi.lister();
            if (r && !r.error) {
                setItems(Array.isArray(r.items) ? r.items : []);
                setNonLues(Number(r.unread) || 0);
                if (r.counts) window.dispatchEvent(new CustomEvent(EVENEMENT_DEMANDES_ADMIN, { detail: r.counts }));
            }
        } catch { /* une notification qui n'arrive pas ne doit rien casser à l'écran */ }
    }, []);

    React.useEffect(() => {
        charger();
        const t = setInterval(charger, INTERVALLE_MS);
        // Après une décision du super-admin (api.ts), on recharge sans attendre la minute suivante.
        const surMaj = () => { charger(); };
        window.addEventListener('demandesAdminMaj', surMaj);
        return () => { clearInterval(t); window.removeEventListener('demandesAdminMaj', surMaj); };
    }, [charger]);

    // Fermer en cliquant ailleurs : un panneau qui reste ouvert derrière le contenu gêne plus qu'il ne sert.
    React.useEffect(() => {
        if (!ouvert) return;
        const surClic = (e: MouseEvent) => {
            const cible = e.target as Node;
            const dansBouton = zone.current?.contains(cible);
            const dansPanneau = panneau.current?.contains(cible);
            if (!dansBouton && !dansPanneau) setOuvert(false);
        };
        document.addEventListener('mousedown', surClic);
        // Le panneau est fixé au document : si la page défile ou change de taille, il faut le remettre
        // en face de son bouton, sinon il flotte tout seul au milieu de l'écran.
        window.addEventListener('resize', calculerPosition);
        window.addEventListener('scroll', calculerPosition, true);
        return () => {
            document.removeEventListener('mousedown', surClic);
            window.removeEventListener('resize', calculerPosition);
            window.removeEventListener('scroll', calculerPosition, true);
        };
    }, [ouvert, calculerPosition]);

    const ouvrir = async () => {
        const prochain = !ouvert;
        if (prochain) calculerPosition();
        setOuvert(prochain);
        if (prochain) { setChargement(true); await charger(); setChargement(false); }
    };

    const cliquer = async (n: Notif) => {
        setOuvert(false);
        if (!n.lu && !estDemandeAdmin(n.type)) {
            setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, lu: true } : x)));
            setNonLues((v) => Math.max(0, v - 1));
            try { await notificationsApi.marquerLue(n.id); } catch { /* sans conséquence : la liste se recalera au prochain chargement */ }
        }
        if (n.link) navigate(n.link);
    };

    const toutLire = async () => {
        setItems((prev) => prev.map((x) => ({ ...x, lu: true })));
        setNonLues(0);
        try { await notificationsApi.toutMarquerLu(); } catch { charger(); }
    };

    return (
        <div className="relative" ref={zone}>
            <button
                type="button"
                onClick={ouvrir}
                aria-label={nonLues ? `Notifications (${nonLues} non lues)` : 'Notifications'}
                className={`relative flex items-center ${compact ? 'justify-center p-2' : 'gap-2 w-full px-3 py-2'} text-[var(--text-secondary)] hover:bg-[var(--interactive-hover)] hover:text-[var(--text-primary)] rounded-lg transition-all font-medium`}
            >
                <Bell size={17} />
                {!compact && <span className="text-[14px]">Notifications</span>}
                {nonLues > 0 && (
                    <span className={`${compact ? 'absolute top-0.5 right-0.5' : 'ml-auto'} min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-black flex items-center justify-center`}>
                        {nonLues > 99 ? '99+' : nonLues}
                    </span>
                )}
            </button>

            {ouvert && position && createPortal((
                <div
                    ref={panneau}
                    style={{ position: 'fixed', left: position.left, top: position.top, bottom: position.bottom, width: Math.min(330, window.innerWidth - 24) }}
                    className="z-[80] rounded-2xl border border-border bg-surface shadow-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                        <p className="text-sm font-bold text-content">Notifications</p>
                        {nonLues > 0 && !items.some((n) => estDemandeAdmin(n.type)) && (
                            <button onClick={toutLire} className="text-[11px] font-bold text-content-muted hover:text-content flex items-center gap-1">
                                <Check size={12} /> Tout marquer comme lu
                            </button>
                        )}
                    </div>

                    <div className="max-h-[380px] overflow-y-auto">
                        {chargement && items.length === 0 && (
                            <div className="flex items-center justify-center py-8"><Loader2 size={16} className="animate-spin text-content-muted" /></div>
                        )}
                        {!chargement && items.length === 0 && (
                            <p className="px-4 py-8 text-center text-xs text-content-muted">
                                Rien pour l'instant. Les demandes de devis, les rendez-vous et les documents signés
                                que vous recevez apparaîtront ici.
                            </p>
                        )}
                        {items.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                onClick={() => cliquer(n)}
                                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-surface-soft transition-colors flex gap-3 ${n.lu ? '' : 'bg-brand-500/[0.06]'}`}
                            >
                                <span className={`mt-0.5 shrink-0 ${n.lu ? 'text-content-muted' : 'text-brand-600 dark:text-brand-400'}`}>{icone(n.type)}</span>
                                <span className="min-w-0 flex-1">
                                    <span className={`block text-[13px] leading-snug ${n.lu ? 'text-content-muted' : 'text-content font-bold'}`}>{n.title}</span>
                                    {n.body && <span className="block text-[11.5px] text-content-muted mt-0.5 leading-snug">{n.body}</span>}
                                    <span className="block text-[10.5px] text-content-muted mt-1">{depuis(n.created_at)}</span>
                                </span>
                                {!n.lu && <span className={`mt-1.5 w-2 h-2 rounded-full bg-red-500 shrink-0 ${estDemandeAdmin(n.type) ? 'animate-pulse' : ''}`} />}
                            </button>
                        ))}
                    </div>
                </div>
            ), document.body)}
        </div>
    );
}
